/**
 * Q4i · how a specification or a limit is printed on every CPV screen — the client twin of the server's
 * services/cpv/limit_text (same rule, same output). Prefer the server's `display` strings where a payload carries them;
 * this formats a raw number the screen holds (a chart legend's line, a view's limits) to the places the server sent.
 *
 *   formatValue(v, decimals)               "20.0" — `decimals` places, trailing zeros kept; shortest exact form when
 *                                          decimals is null; "—" for no value
 *   rangeText(lo, hi, decimals, unit)      "20.0 – 40.0 °C", "≤ 0.50 % w/w", "≥ 98.0 %", "—" (no unit)
 *   shownOr(display, key, fallback)        the server's display string, else the fallback
 *
 * The places are the parameter's (`display.decimals`); a control limit or centre line uses `display.limit_decimals`
 * (the plant's control_tower.stats.limit_extra_decimals more). Rounding is half up on the decimal text itself — a
 * number is read through its shortest round-trip text (as the server reads a float), never rounded as a binary float.
 */
export const NONE_TEXT = '—';
export const RANGE_SEP = ' – ';
export const AT_MOST = '≤';
export const AT_LEAST = '≥';

const has = (v) => v !== null && v !== undefined && v !== '';

/** {sign, int, frac} of a decimal text (exponent form allowed), or null when it is not a finite number. */
function parts(v) {
  if (!has(v) || typeof v === 'boolean') return null;
  if (typeof v === 'number' && !Number.isFinite(v)) return null;
  const m = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/.exec(String(v).trim());
  if (!m || (!m[2] && !m[3])) return null;
  let int = m[2] || '0';
  let frac = m[3] || '';
  const exp = Number(m[4] || 0);
  if (exp > 0) { frac = frac.padEnd(exp, '0'); int += frac.slice(0, exp); frac = frac.slice(exp); }
  if (exp < 0) { int = int.padStart(-exp + 1, '0'); frac = int.slice(int.length + exp) + frac; int = int.slice(0, int.length + exp); }
  return { sign: m[1], int, frac };
}

/** `v` at `decimals` places (half up, trailing zeros kept), its shortest exact form when `decimals` is null. */
export function formatValue(v, decimals) {
  const p = parts(v);
  if (!p) return NONE_TEXT;
  let { int, frac } = p;
  const dp = Number.isInteger(decimals) && decimals >= 0 ? decimals : null;
  if (dp === null) {
    frac = frac.replace(/0+$/, '');
  } else {
    if (frac.length > dp) {
      const digits = (int + frac.slice(0, dp)).split('').map(Number);
      if (Number(frac[dp]) >= 5) {
        let i = digits.length - 1;
        while (i >= 0 && digits[i] === 9) { digits[i] = 0; i -= 1; }
        if (i < 0) digits.unshift(1); else digits[i] += 1;
      }
      const all = digits.join('');
      int = all.slice(0, all.length - dp);
      frac = all.slice(all.length - dp);
    }
    frac = frac.padEnd(dp, '0');
  }
  int = int.replace(/^0+(?=\d)/, '') || '0';
  const out = frac ? `${int}.${frac}` : int;
  return p.sign === '-' && /[1-9]/.test(out) ? `-${out}` : out;
}

/** "lo – hi", "≤ hi", "≥ lo" at `decimals`, the unit after it; "—" (no unit) when neither end is set. */
export function rangeText(lo, hi, decimals, unit = '') {
  const a = parts(lo) ? formatValue(lo, decimals) : null;
  const b = parts(hi) ? formatValue(hi, decimals) : null;
  const text = a !== null && b !== null ? `${a}${RANGE_SEP}${b}` : b !== null ? `${AT_MOST} ${b}` : a !== null ? `${AT_LEAST} ${a}` : null;
  if (text === null) return NONE_TEXT;
  return unit ? `${text} ${unit}` : text;
}

/** The server's display string `key` when it sent one, else `fallback` (a payload built before the server carried it). */
export function shownOr(display, key, fallback) {
  const s = display && display[key];
  return has(s) ? s : fallback;
}
